import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { BASE_URL } from "../../../constant/constant";
import GenericActionBar from "../../../Layouts/GenericActionBar/GenericActionBar";
import SingleSelectBox from "../../../components/FormComponents/SingleSelectBox";
import authService from "../../../services/authService";
import axiosInstance from "../../../services/axiosService";
import useDirtyChecker from "../../../hooks/useDirtyChecker";
import ButtonActionIcon from "../../../components/BuilderComponents/ButtonActionIcon";
import Button from "../../../components/FormComponents/Button";

const PipelinesApiSection = ({ file }) => {
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [availableVersions, setAvailableVersions] = useState([]);
  const [selectedDropdownValue, setSelectedDropdownValue] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);

  const dirtyCheckConfig = useMemo(() => ({ items: selectedVersions }), [selectedVersions]);
  const { isDirty, markClean } = useDirtyChecker(dirtyCheckConfig);

  const { appId } = useParams();
  const activeAppId = appId || file?.id;

  useEffect(() => {
    const initData = async () => {
      if (!activeAppId) return;

      try {
        setIsLoading(true);

        const allVersionsResponse = await fetchVersions();
        const availableOptions = allVersionsResponse.data?.data || [];
        setAvailableVersions(availableOptions);

        let savedItems = [];

        try {
          const savedConfigResponse = await axiosInstance.get(`/pipeline/${activeAppId}`);
          savedItems = savedConfigResponse.data?.data?.items || savedConfigResponse.data?.items || [];
        } catch (err) {
          if (err.response?.status === 404) {
            savedItems = [];
          } else {
            throw err;
          }
        }

        const hydratedData = savedItems.map((savedItem) => {
          const match = availableOptions.find(
            (option) => option.template_id === savedItem.template_id && option.id === savedItem.version_id
          );

          if (match) {
            return {
              template_id: savedItem.template_id,
              version_id: savedItem.version_id,
              name: match.name,
              template_name: match.template_name
            };
          }

          return {
            ...savedItem,
            name: "Unknown Version",
            template_name: "Unknown Template"
          };
        });

        setSelectedVersions(hydratedData);
        setHasInitialized(true);
        // Set the initial baseline for dirty checker after loading data
      } catch (error) {
        console.error("Error initializing pipeline data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAppId]);

  useEffect(() => {
    if (hasInitialized) {
      markClean();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInitialized]);

  const fetchVersions = async () => {
    const token = authService.getLocalToken();
    const response = await axios.get(`${BASE_URL}/pipelines/api/v1/ct-template-versions`, {
      headers: {
        Authorization: token,
        "Content-Type": "application/json"
      }
    });
    return response;
  };

  // Transform available versions to include composite label for display
  const transformedOptions = availableVersions.map((version) => ({
    ...version,
    composite_label: `${version.template_name} - ${version.name}`,
    unique_id: `${version.template_id}-${version.id}`
  }));

  const handleSelectChange = (selectedOption) => {
    setSelectedDropdownValue(selectedOption);
  };

  const handleAddVersion = () => {
    if (!selectedDropdownValue) return;

    const alreadyExists = selectedVersions.some(
      (v) => v.template_id === selectedDropdownValue.template_id && v.version_id === selectedDropdownValue.id
    );

    if (alreadyExists) {
      setSelectedDropdownValue(null);
      return;
    }

    const newVersion = {
      template_id: selectedDropdownValue.template_id,
      version_id: selectedDropdownValue.id,
      name: selectedDropdownValue.name,
      template_name: selectedDropdownValue.template_name
    };

    setSelectedVersions([...selectedVersions, newVersion]);
    setSelectedDropdownValue(null);
  };

  const handleDeleteVersion = (templateId, versionId) => {
    setSelectedVersions(selectedVersions.filter((v) => !(v.template_id === templateId && v.version_id === versionId)));
  };

  const handleSaveWebhook = async () => {
    try {
      setIsSaving(true);
      const payloadItems = selectedVersions.map((item) => ({
        template_id: item.template_id,
        version_id: item.version_id
      }));

      const payload = {
        items: payloadItems
      };

      await axiosInstance.post(`/pipeline/${activeAppId}`, payload);
      markClean();
      console.log("Saved successfully");
    } catch (error) {
      console.error("Error saving pipeline versions:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="h_100 d_flex flex_column">
      <GenericActionBar
        path={
          <div>
            <em>API</em> → <span>{file?.name || "Pipelines"}</span>
          </div>
        }
        actions={[
          {
            key: "save",
            icon: "save",
            title: isSaving ? "Saving..." : "Save",
            disabled: isSaving || !isDirty,
            onClick: handleSaveWebhook
          }
        ]}
        options={{
          save: {
            className: `${isDirty ? "save_glowing" : ""} ${isSaving ? "disabled" : ""}`.trim()
          }
        }}
      />

      <div className="h_100 overflow_scroll">
        <div
          className="p_3 m_auto mt_2 page_main_section"
          style={{
            maxWidth: "800px",
            padding: "24px",
            marginTop: "16px",
            paddingBottom: "100px"
          }}
        >
          <h2 style={{ color: "#333", marginBottom: "24px" }}>Pipeline Configuration</h2>

          <div style={{ marginBottom: "24px" }}>
            <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
              <div style={{ flex: 1 }} onKeyDown={(e) => e.key === "Enter" && handleAddVersion()}>
                <SingleSelectBox
                  options={transformedOptions}
                  valueKey="unique_id"
                  labelKey={["composite_label"]}
                  selectedData={selectedDropdownValue}
                  onChange={handleSelectChange}
                  label="Select Pipeline Versions"
                  placeholder="Search and select a version..."
                  isSearch={true}
                />
              </div>

              <Button onClick={handleAddVersion} disabled={!selectedDropdownValue || isLoading} className="mt_4">
                Add
              </Button>
            </div>
          </div>

          {selectedVersions.length > 0 && (
            <div style={{ marginTop: "24px" }}>
              <h3
                style={{
                  fontSize: "14px",
                  color: "#666",
                  marginBottom: "12px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px"
                }}
              >
                Selected Pipeline Versions
              </h3>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                  gap: "12px",
                  padding: "4px"
                }}
              >
                {selectedVersions.map((item) => (
                  <div
                    key={`${item.template_id}-${item.version_id}`}
                    style={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "6px",
                      padding: "12px",
                      background: "#fff",
                      transition: "box-shadow 0.2s",
                      position: "relative"
                    }}
                  >
                    <div className="d_flex justify_between align_top">
                      <div>
                        <div
                          style={{
                            fontWeight: "600",
                            color: "#333",
                            fontSize: "14px",
                            paddingRight: "60px"
                          }}
                        >
                          {item.template_name}
                        </div>
                        <div
                          style={{
                            color: "#888",
                            fontSize: "12px",
                            marginTop: "4px"
                          }}
                        >
                          {item.name}
                        </div>
                      </div>

                      <ButtonActionIcon
                        onClick={() => handleDeleteVersion(item.template_id, item.version_id)}
                        type="delete"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PipelinesApiSection;
